import React, { useState, useEffect } from "react";
import { FaCamera, FaUserPlus } from "react-icons/fa";
import profileImg from "../../assets/default.jpeg";
import { getSingleAdmin, updateAdminDetails, updateProfileImage, registerAdmin } from "../../utils/admin";
import { jwtDecode } from "jwt-decode";
import { toast } from "sonner";

const Card = ({ children, className }) => (
    <div className={`bg-white rounded-lg shadow-md ${className}`}>{children}</div>
);

const CardContent = ({ children }) => <div className="p-4 sm:p-6">{children}</div>;

const Button = ({ children, onClick, className, disabled }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={`px-4 py-2 text-white font-semibold rounded flex justify-center items-center gap-2 ${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
    >
        {children}
    </button>
);

const Input = ({ id, type, value, onChange, className, defaultValue, disabled }) => (
    <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        defaultValue={defaultValue}
        disabled={disabled}
        className={`w-full p-2 border rounded ${className} ${disabled ? "bg-gray-200" : ""}`}
    />
);

const Label = ({ children, htmlFor }) => (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700">
        {children}
    </label>
);

export default function ProfilePage() {
    const [adminData, setAdminData] = useState({});
    const [profilePic, setProfilePic] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    // New states for creating admin
    const [newAdmin, setNewAdmin] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        role: "admin",
    });
    const [adding, setAdding] = useState(false);

    const token = localStorage.getItem("token");
    const decoded = jwtDecode(token);
    const adminId = decoded.id;
    const adminType = decoded.role;
    const isSuperAdmin = adminType === "super_admin";

    useEffect(() => {
        const fetchAdmin = async () => {
            try {
                const admin = await getSingleAdmin(adminId, token);
                setAdminData(admin);
                setName(`${admin.admin?.firstName} ${admin.admin?.lastName}` || "");
                setProfilePic(admin.admin?.profile_image);
            } catch (error) {
                console.error("Error fetching admin details:", error);
            }
        };
        fetchAdmin();
    }, [adminId, token]);

    const handleProfilePicChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append("profile_image", file);

        try {
            const response = await updateProfileImage(adminId, formData, token);
            if (response.success) {
                toast.success("Profile image updated");
                setProfilePic(response.profile_image);
            } else {
                toast.error("Couldn't update profile image", { description: response.message });
            }
        } catch (error) {
            console.error("Error updating profile image:", error);
            toast.error("Couldn't update profile image", { description: error?.message });
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (password && password !== confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }

        setLoading(true);

        const nameParts = name.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        try {
            const updatedData = { firstName, lastName };
            if (password) updatedData.password = password;

            const response = await updateAdminDetails(adminId, updatedData, token);

            if (response.success) {
                toast.success("Profile updated");
                setPassword("");
                setConfirmPassword("");
                setAdminData((prev) => ({
                    ...prev,
                    admin: { ...prev.admin, ...updatedData },
                }));
                setEditing(false);
            } else {
                toast.error("Couldn't update profile", { description: response.message });
            }
        } catch (error) {
            console.error("Error updating admin details:", error);
            toast.error("Couldn't update profile", { description: error?.message });
        } finally {
            setLoading(false);
        }
    };

    // New function: handle adding another admin
    const handleAddAdmin = async () => {
        if (!newAdmin.firstName || !newAdmin.lastName || !newAdmin.email || !newAdmin.password) {
            toast.error("Please fill in all fields");
            return;
        }

        setAdding(true);
        try {
            const response = await registerAdmin(newAdmin, token);

            if (response.admin) {
                toast.success("New admin added", {
                    description: `${newAdmin.firstName} ${newAdmin.lastName} can now log in.`,
                });
                setNewAdmin({ firstName: "", lastName: "", email: "", password: "", role: "admin" });
            } else {
                // Structured error: headline message + the backend detail
                // (e.g. which unique key collided) as the description.
                toast.error(response.message || "Failed to add admin", { description: response.error });
            }
        } catch (error) {
            console.error("Error adding admin:", error);
            toast.error("Couldn't add admin", { description: error?.message });
        } finally {
            setAdding(false);
        }
    };

    return (
        <div>
            <Card className="max-w-xl mx-auto mb-6">
                <CardContent>
                    <div className="flex flex-col items-center">
                        <div className="relative w-32 h-32 mb-4">
                            <img
                                src={profilePic || profileImg}
                                alt="Profile"
                                className="w-full h-full rounded-full border border-gray-300 object-cover"
                            />
                            <label
                                htmlFor="profilePic"
                                className="absolute bottom-0 right-0 bg-blue-500 text-white p-2 rounded-full cursor-pointer hover:bg-blue-600"
                            >
                                {uploading ? (
                                    <svg
                                        className="animate-spin h-5 w-5 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                        />
                                    </svg>
                                ) : (
                                    <FaCamera />
                                )}
                                <input
                                    id="profilePic"
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleProfilePicChange}
                                />
                            </label>
                        </div>

                        <h2 className="text-xl font-semibold mb-4">
                            {adminData?.admin?.firstName + " " + adminData?.admin?.lastName || "Loading..."}
                        </h2>

                        {isSuperAdmin && (
                            <div className="w-full mb-4">
                                <Label htmlFor="name">Name</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    disabled={!editing || loading}
                                    className="mt-1"
                                />
                            </div>
                        )}

                        <div className="w-full mb-4">
                            <Label htmlFor="password">New Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                className="mt-1"
                            />
                        </div>

                        <div className="w-full mb-4">
                            <Label htmlFor="confirmPassword">Confirm Password</Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={loading}
                                className="mt-1"
                            />
                        </div>

                        {isSuperAdmin && (
                            <Button
                                onClick={() => setEditing(!editing)}
                                className="mb-4 bg-blue-500 hover:bg-blue-600"
                                disabled={loading}
                            >
                                {editing ? "Stop Editing" : "Edit Details"}
                            </Button>
                        )}

                        <Button onClick={handleSave} className="bg-blue-500 hover:bg-blue-600" disabled={loading}>
                            {loading ? (
                                <>
                                    <svg
                                        className="animate-spin h-5 w-5 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                        />
                                    </svg>
                                    Saving...
                                </>
                            ) : (
                                "Save Changes"
                            )}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {isSuperAdmin && (
                <Card className="max-w-xl mx-auto">
                    <CardContent>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-blue-600">
                            <FaUserPlus /> Add New Admin
                        </h3>
                        <div className="grid gap-4">
                            <div>
                                <Label>First Name</Label>
                                <Input
                                    type="text"
                                    value={newAdmin.firstName}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, firstName: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Last Name</Label>
                                <Input
                                    type="text"
                                    value={newAdmin.lastName}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, lastName: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={newAdmin.email}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Password</Label>
                                <Input
                                    type="password"
                                    value={newAdmin.password}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Role</Label>
                                <select
                                    value={newAdmin.role}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                                    className="w-full p-2 border rounded"
                                >
                                    <option value="admin">Admin</option>
                                    <option value="super_admin">Super Admin</option>
                                </select>
                            </div>

                            <Button
                                onClick={handleAddAdmin}
                                className="bg-green-600 hover:bg-green-700 mt-2"
                                disabled={adding}
                            >
                                {adding ? (
                                    <>
                                        <svg
                                            className="animate-spin h-5 w-5 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                            />
                                        </svg>
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <FaUserPlus /> Add Admin
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
